import React, { useEffect, useState } from 'react';
import { Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ComposedChart, ReferenceLine } from 'recharts';
import dayjs from 'dayjs';
import { Tooltip as Tooltip2 } from '@material-ui/core';

import { estimateExpirationDate, estimateTotalUsage } from '../../../../helpers';
import styles from './UploadHoursChart.module.scss';

type THoursLimitGraph = {
  limit: number;
  used: number;
  startDate: string;
  endDate: string;
  ratio: string;
  usageData?: any;
  /* example: year/month: usage
    {"2024-10": 5, "2024-11": 10, "2024-12": 13, "2025-01": 16, "2025-02": 10}
  */
  projected_hours_at_the_end?: number;
  deletedHoursCurrentPeriod?: number;
};

type UsageData = Record<string, number>;

type AverageUsageData = { month: string; uploadHours: number; average?: number; finish?: number }[];

const calculateBiMonthlyAverage = (usageData: UsageData): AverageUsageData => {
  const entries = Object.entries(usageData);
  const result: AverageUsageData = [];

  for (let i = 0; i < entries.length; i++) {
    const [month, usage] = entries[i];
    const prevUsage = i > 0 ? entries[i - 1][1] : null;
    const average = prevUsage !== null ? (prevUsage + usage) / 2 : undefined;
    if (i === 0) {
      result.push({ month, uploadHours: usage, average: usage });
    } else {
      result.push({ month, uploadHours: usage, average });
    }
  }

  return result;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  const item: any = payload[0];
  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{ background: '#fff', padding: '5px', border: '1px solid #ccc', color: 'black', borderRadius: 'var(--ui-border-radius)' }}
      >
        {item.dataKey === 'finish' ? (
          // Custom text for "finish" bar
          <div>
            End date: {label}
            <p style={{ color: item.fill }}>{`Projected total number of hours used: ${item.value.toFixed(1)}`}</p>
          </div>
        ) : (
          <div>
            <p>{`Month: ${label}`}</p>
            <p style={{ color: item.fill }}>
              {`Used : ${item.value.toFixed(1)} hours`}
              <br />
              <div style={{ color: '#ffc658' }}>{`Average : ${item.payload.average.toFixed(1)} hours`}</div>
            </p>
          </div>
        )}
      </div>
    );
  }
  return null;
};

const HoursLimitGraph: React.FC<THoursLimitGraph> = ({
  limit,
  used,
  startDate,
  endDate,
  usageData,
  ratio,
  projected_hours_at_the_end,
  deletedHoursCurrentPeriod,
}) => {
  const [expirationDate, setExpDate] = useState('');
  const [chartData, setChartData] = useState<any>([]);
  const currentMonth = new Date()?.toISOString()?.slice(0, 7);

  const [estimatedEndUsage, setEstimatedEndUsage] = useState(0);
  useEffect(() => {
    const res = estimateExpirationDate({
      monthly_hours_usage: usageData,
      hours_limit: limit,
      hours_limit_used: used,
      period_start: startDate,
      period_end: endDate,
    });

    const averageUsageLine = calculateBiMonthlyAverage(usageData);
    const estimatedUsageByPeriodEnd = estimateTotalUsage({
      monthly_hours_usage: usageData,
      hours_limit: limit,
      hours_limit_used: used,
      period_start: startDate,
      period_end: endDate,
    });
    setEstimatedEndUsage(estimatedUsageByPeriodEnd);

    if (res) {
      // @ts-ignore
      averageUsageLine.push({
        month: res,
        finish: estimatedUsageByPeriodEnd,
      });
      setExpDate(res);
    }

    setChartData(averageUsageLine);
  }, [limit, used, startDate, endDate, usageData]);

  return (
    <div className={styles.chartContainer}>
      <div className={styles.sidebar}></div>
      <div className={styles.chart}>
        <h3>Upload Hours</h3>
        <h3>Limit: {limit} hour(s)</h3>
        <p>Start Date: {`${dayjs(startDate).format('DD-MM-YYYY')}`}</p>
        <p>End Date: {`${dayjs(endDate).format('DD-MM-YYYY')}`}</p>
        <p>
          Upload Hours Used: {used.toFixed(1)} hrs out of {limit}h ({ratio}%)
        </p>
        {typeof deletedHoursCurrentPeriod === 'number' && (
          <Tooltip2
            title={`
              Deleted upload hours (in minutes):${(deletedHoursCurrentPeriod * 60).toFixed(0)} min`}
          >
            <div>
              <p>Deleted upload hours (current period): {deletedHoursCurrentPeriod.toFixed(1)} hrs</p>
            </div>
          </Tooltip2>
        )}
        <p>Projected total number of hours used by End date: {estimatedEndUsage?.toFixed(1)}</p>
        {!!Object.getOwnPropertyNames(usageData).length && expirationDate && (
          <>
            <p style={{ color: 'red' }}>Projected hours Expiry Date: {expirationDate} </p>
            <p style={{ color: 'red' }}>Projected Expiry Date hours used: {projected_hours_at_the_end} </p>
          </>
        )}
        {!!Object.getOwnPropertyNames(usageData).length && (
          <ResponsiveContainer width={400} height={300}>
            <ComposedChart data={chartData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="uploadHours" fill="#8884d8" />
              <ReferenceLine x={currentMonth} stroke="blue" label="Today" />
              <Bar dataKey="finish" fill="#ff0000" />
              <Line type="monotone" dataKey="average" stroke="#ffc658" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default HoursLimitGraph;
